// src/utils/errorMessages.js

/**
 * Centralized error message translation utility
 * Converts technical error messages to user-friendly ones
 */

/**
 * Maps technical error patterns to user-friendly messages
 */
const ERROR_PATTERNS = {
  // Authentication errors
  'frappe.exceptions.authenticationerror': 'Invalid email or password. Please check your credentials and try again.',
  'authenticationerror': 'Invalid email or password. Please check your credentials and try again.',
  'authentication error': 'Invalid email or password. Please check your credentials and try again.',
  'invalid login credentials': 'Invalid email or password. Please check your credentials and try again.',
  'invalid credentials': 'Invalid email or password. Please check your credentials and try again.',
  'incorrect password': 'Invalid email or password. Please check your credentials and try again.',
  'wrong password': 'Invalid email or password. Please check your credentials and try again.',
  'user not found': 'Invalid email or password. Please check your credentials and try again.',
  'email not found': 'Invalid email or password. Please check your credentials and try again.',
  'account not found': 'Invalid email or password. Please check your credentials and try again.',

  // Session errors
  'session expired': 'Your session has expired. Please sign in again.',
  'session timeout': 'Your session has timed out. Please sign in again.',
  'session invalid': 'Your session is invalid. Please sign in again.',
  'not authenticated': 'You are not signed in. Please sign in to continue.',
  'unauthorized': 'You are not authorized to perform this action.',
  'forbidden': 'You do not have permission to access this resource.',

  // Network errors
  'network error': 'Network connection error. Please check your internet connection and try again.',
  'timeout': 'Request timed out. Please try again.',
  'connection refused': 'Unable to connect to the server. Please try again later.',
  'failed to fetch': 'Unable to connect to the server. Please check your internet connection.',

  // Validation errors
  'required field': 'Please fill in all required fields.',
  'invalid email': 'Please enter a valid email address.',
  'invalid format': 'The information you entered is not in the correct format.',
  'validation error': 'Please check the information you entered and try again.',

  // Server errors
  'internal server error': 'An unexpected error occurred. Please try again later.',
  'server error': 'The server encountered an error. Please try again later.',
  'service unavailable': 'The service is temporarily unavailable. Please try again later.',
  'bad gateway': 'The server is temporarily unavailable. Please try again later.',

  // File upload errors
  'file too large': 'The file you selected is too large. Please choose a smaller file.',
  'invalid file type': 'The file type is not supported. Please choose a different file.',
  'upload failed': 'File upload failed. Please try again.',

  // Generic errors
  'not found': 'The requested resource was not found.',
  'already exists': 'This item already exists.',
  'duplicate entry': 'This entry already exists.',
};

/**
 * Common HTTP status code messages
 */
const STATUS_MESSAGES = {
  400: 'Invalid request. Please check the information you entered.',
  401: 'Invalid email or password. Please check your credentials and try again.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested resource was not found.',
  408: 'Request timed out. Please try again.',
  409: 'This item already exists.',
  422: 'The information you entered is not valid. Please check and try again.',
  429: 'Too many requests. Please wait a moment and try again.',
  500: 'An unexpected error occurred. Please try again later.',
  502: 'The server is temporarily unavailable. Please try again later.',
  503: 'The service is temporarily unavailable. Please try again later.',
  504: 'Request timed out. Please try again.',
};

/**
 * Extract error message from various error formats
 */
function extractErrorMessage(error) {
  if (!error) return 'An unexpected error occurred. Please try again.';

  // Try response.data first (axios errors)
  if (error.response?.data) {
    const data = error.response.data;

    // Check for message field
    if (data.message) {
      return String(data.message);
    }

    // Check for exception field
    if (data.exception || data.exc) {
      return String(data.exception || data.exc);
    }

    // Check for _server_messages (Frappe format)
    if (data._server_messages) {
      try {
        const serverMsgs = typeof data._server_messages === 'string'
          ? JSON.parse(data._server_messages)
          : data._server_messages;

        if (Array.isArray(serverMsgs) && serverMsgs.length > 0) {
          const firstMsg = typeof serverMsgs[0] === 'string'
            ? JSON.parse(serverMsgs[0])
            : serverMsgs[0];

          if (firstMsg?.message) {
            return String(firstMsg.message);
          }
        }
      } catch (e) {
        // If parsing fails, continue to other checks
      }
    }

    // Check for error field
    if (data.error) {
      return String(data.error);
    }
  }

  // Try error.message
  if (error.message) {
    return String(error.message);
  }

  // Try error itself if it's a string
  if (typeof error === 'string') {
    return error;
  }

  return 'An unexpected error occurred. Please try again.';
}

/**
 * Translate technical error message to user-friendly message
 */
function translateError(errorMessage) {
  if (!errorMessage || typeof errorMessage !== 'string') {
    return 'An unexpected error occurred. Please try again.';
  }

  const lowerMessage = errorMessage.toLowerCase().trim();

  // Check against error patterns
  for (const [pattern, friendlyMessage] of Object.entries(ERROR_PATTERNS)) {
    if (lowerMessage.includes(pattern)) {
      return friendlyMessage;
    }
  }

  // If no pattern matches, return the original message
  // but clean up common technical prefixes
  let cleaned = errorMessage;

  // Remove common technical prefixes
  cleaned = cleaned.replace(/^(frappe\.exceptions\.|error:\s*|exception:\s*)/i, '');

  // Capitalize first letter
  cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);

  return cleaned;
}

/**
 * Parse login-specific errors to distinguish between invalid email and invalid password
 * @param {Error|Object|string} error - Error object, response object, or error string
 * @returns {string} Specific error message for login errors
 */
export function getLoginError(error) {
  if (!error) return 'Invalid email or password. Please check your credentials and try again.';

  const errorMessage = extractErrorMessage(error);
  if (!errorMessage || typeof errorMessage !== 'string') {
    return 'Invalid email or password. Please check your credentials and try again.';
  }

  const lowerMessage = errorMessage.toLowerCase().trim();

  // Check for specific password errors FIRST (more specific patterns)
  // These patterns clearly indicate password issues
  if (
    lowerMessage.includes('incorrect password') ||
    lowerMessage.includes('wrong password') ||
    lowerMessage.includes('password incorrect') ||
    lowerMessage.includes('password does not match') ||
    lowerMessage.includes('password mismatch') ||
    lowerMessage.includes('password is incorrect') ||
    lowerMessage.includes('password is wrong') ||
    lowerMessage.includes('pwd') && (lowerMessage.includes('incorrect') || lowerMessage.includes('wrong') || lowerMessage.includes('invalid'))
  ) {
    return 'Invalid password. Please check your password and try again.';
  }

  // Check for "invalid password" - but only if it's not part of a generic message
  if (
    lowerMessage.includes('invalid password') &&
    !lowerMessage.includes('invalid login credentials') &&
    !lowerMessage.includes('invalid email or password')
  ) {
    return 'Invalid password. Please check your password and try again.';
  }

  // Check for specific email/user not found errors (more specific patterns)
  // These patterns clearly indicate email/user issues
  if (
    lowerMessage.includes('user not found') ||
    lowerMessage.includes('email not found') ||
    lowerMessage.includes('account not found') ||
    lowerMessage.includes('no user found') ||
    lowerMessage.includes('user does not exist') ||
    lowerMessage.includes('user does not exist') ||
    lowerMessage.includes('no account found') ||
    (lowerMessage.includes('invalid email') && !lowerMessage.includes('password')) ||
    (lowerMessage.includes('email invalid') && !lowerMessage.includes('password')) ||
    (lowerMessage.includes('email') && lowerMessage.includes('not found')) ||
    (lowerMessage.includes('usr') && (lowerMessage.includes('not found') || lowerMessage.includes('invalid')))
  ) {
    return 'Invalid email. Please check your email address and try again.';
  }

  // Check for generic "invalid login credentials" - we can't distinguish, so show generic
  if (
    lowerMessage.includes('invalid login credentials') ||
    lowerMessage.includes('invalid credentials') ||
    lowerMessage.includes('authentication failed') ||
    lowerMessage.includes('authentication error')
  ) {
    return 'Invalid email or password. Please check your credentials and try again.';
  }

  // If the error message contains "password" but not "email", assume password error
  if (lowerMessage.includes('password') && !lowerMessage.includes('email')) {
    return 'Invalid password. Please check your password and try again.';
  }

  // If the error message contains "email" or "user" but not "password", assume email error
  if ((lowerMessage.includes('email') || lowerMessage.includes('user') || lowerMessage.includes('usr')) && !lowerMessage.includes('password')) {
    return 'Invalid email. Please check your email address and try again.';
  }

  // Generic login error fallback
  return 'Invalid email or password. Please check your credentials and try again.';
}

/**
 * Get user-friendly error message from error object
 * @param {Error|Object|string} error - Error object, response object, or error string
 * @param {number} statusCode - Optional HTTP status code
 * @returns {string} User-friendly error message
 */
export function getUserFriendlyError(error, statusCode = null) {
  // If status code is provided and we have a message for it, use that
  if (statusCode && STATUS_MESSAGES[statusCode]) {
    return STATUS_MESSAGES[statusCode];
  }

  // Extract error message from error object
  const errorMessage = extractErrorMessage(error);

  // Translate to user-friendly message
  return translateError(errorMessage);
}

/**
 * Get error message with fallback to status code message
 */
export function getErrorWithStatusFallback(error) {
  const statusCode = error?.response?.status || error?.status;
  const message = getUserFriendlyError(error, statusCode);

  // If we got a status code message but the error has more specific info, prefer the specific message
  if (statusCode && STATUS_MESSAGES[statusCode] && message === STATUS_MESSAGES[statusCode]) {
    // Check if we can extract a more specific message
    const extracted = extractErrorMessage(error);
    if (extracted && extracted.toLowerCase() !== 'request failed') {
      const translated = translateError(extracted);
      if (translated !== extracted) {
        return translated;
      }
    }
  }

  return message;
}

const ErrorMessages = {
  getUserFriendlyError,
  getErrorWithStatusFallback,
  translateError,
  extractErrorMessage,
};

export default ErrorMessages;
